import * as React from "react";
import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { decode } from "html-entities";

import { getOnlineNode } from "../../../core/getNode";
import { alertMessages } from "../../../partials/alertMessages";
import { useEmailDataContext } from "../../../partials/dataContext";
import Pagination from "../../../partials/Pagination";
import MessageBox from "../../../partials/MessageBox";
import EventItem from "./EventItem";
import SelectedEventItem from "./SelectedEventItem";
import LoadingSpinner from "../../../partials/loading";
import { getContentTypeByBundle, getTemplateByIdentity } from "../../../utils/helpers";

import { Button, Select, FormControl, InputLabel, MenuItem } from "@mui/material";

export default function Events(props) {
  const [eventCategory, setEventCategory] = useState(0);

  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 12;

  const [categories, setCategories] = useState([]);
  const [selectedEventItems, setSelectedEventItems] = useState([]);

  const [data, setData] = useState([]);

  const [messageBoxVisible, setMessageBoxVisible] = useState(false);
  const [messageTitle, setMessageTitle] = useState("");
  const [messageBody, setMessageBody] = useState("");

  const { selectedTemplate, contentTypeSelected, setEventsObject } = useEmailDataContext();

  // redux
  const system = useSelector((state) => state).systemReducer;
  const config = useSelector((state) => state.configReducer);
  const templateData = getTemplateByIdentity(selectedTemplate, config.templates);
  const contentTypeData = getContentTypeByBundle(contentTypeSelected, templateData.contentTypes);

  useEffect(() => {
    getCategories();
  }, []);

  useEffect(() => {
    getContent();
  }, [currentPage]);

  useEffect(() => {
    if (selectedEventItems.length > 0) {
      setEventsObject(selectedEventItems.map((item) => item.nid));
    } else {
      setEventsObject([]);
    }
  }, [selectedEventItems]);

  const messageBoxState = (visible, type, title, body) => {
    setMessageBoxVisible(visible);
    setMessageTitle(title);
    setMessageBody(body);
  };

  const getCategories = () => {
    setIsLoading(true);

    getOnlineNode("api/v2/taxonomies/event_categories")
      .then((response) => {
        renderCategories(response.data);
      })
      .catch((_error) => {
        if (_error.response) {
          messageBoxState(true, "error", "Error", _error.response.statusText);
        } else if (_error.request) {
          messageBoxState(true, "error", "Error", alertMessages.requestError.message);
        } else {
          messageBoxState(true, "error", "Error", alertMessages.unknownError.message);
        }
        setIsLoading(false);
      });
  };

  const renderCategories = (_data) => {
    let _categories = [
      {
        value: 0,
        label: "--- All ---",
      },
    ];

    _data.forEach((item) => {
      _categories.push({
        value: item?.tid[0].value,
        label: item?.name[0].value,
      });
    });
    setCategories(_categories);
  };

  const getContent = () => {
    setIsLoading(true);
    messageBoxState(false, "", "", "");

    let path = `api/v2/events?status=1&promote=1&items_per_page=${pageSize}&page=${currentPage - 1}`;

    const tid = eventCategory;

    if (tid !== 0) {
      path = `api/v2/events/${tid}/all?status=1&promote=1&items_per_page=${pageSize}&page=${currentPage - 1}`;
    }

    getOnlineNode(path)
      .then((response) => {
        setData(response.data.rows);
        setIsLoading(false);
      })
      .catch((_error) => {
        if (_error.response) {
          messageBoxState(true, "error", "Error", _error.response.statusText);
        } else if (_error.request) {
          messageBoxState(true, "error", "Error", alertMessages.requestError.message);
        } else {
          messageBoxState(true, "error", "Error", alertMessages.unknownError.message);
        }
        setIsLoading(false);
      });
  };

  const addSelectedEventItem = (_data) => {
    if (
      !(
        selectedEventItems.filter((item) => {
          return item.nid === _data.nid;
        }).length > 0
      )
    ) {
      setSelectedEventItems((prevItems) => [...prevItems, _data]);
    }
  };

  const removeSelectedEventItem = (event) => {
    setSelectedEventItems((prevItems) => prevItems.filter((item) => item.nid !== event.nid));
  };

  const renderPagination = () => {
    if (data.length && data.length > 0) {
      return (
        <Pagination
          containerClasses={`d-flex justify-content-center align-items-center`}
          previousDisabled={currentPage === 1 ? true : false}
          nextDisabled={data?.length < pageSize ? true : false}
          currentPage={currentPage}
          onPrevious={() => {
            const current = currentPage - 1;
            setCurrentPage(current);
          }}
          onNext={() => {
            const current = currentPage + 1;
            setCurrentPage(current);
          }}
        />
      );
    }
  };

  const updateEventItemView = (event) => {
    if (event) {
      event.preventDefault();
    }
    if (currentPage > 1) {
      setCurrentPage(1);
    } else {
      getContent();
    }
  };

  const removeAllSelectedEventItems = (event) => {
    if (event) {
      event.preventDefault();
    }
    setSelectedEventItems([]);
  };

  const renderCategoryList = categories.map((category) => {
    return (
      <MenuItem
        key={`category-${category.value}`}
        value={category.value}
        // sx={{ display: "none" }}
        onClick={(e) => {
          setEventCategory(e.target.value);
        }}
      >
        {decode(category.label)}
      </MenuItem>
    );
  });

  const renderEventItems = data.map((event) => (
    <EventItem
      key={`event-item-${event.nid}`}
      event={event}
      selectedEvents={selectedEventItems}
      addSelectedEventItem={(item) => addSelectedEventItem(item)}
      removeSelectedEventItem={(item) => removeSelectedEventItem(item)}
      eventImage={`https://${system.subdomain}.companyapp.net${event.field_featured_image}`}
    />
  ));

  return (
    <div className="event-view-wrapper">
      {isLoading && (
        <div
          className="w-100 h-100 d-flex align-items-center justify-content-center"
          style={{ position: "absolute", top: 0, left: 0, backgroundColor: "rgba(255,255,255,0.5)", zIndex: 9999 }}
        >
          <LoadingSpinner />
        </div>
      )}
      <div className="message-box-wrapper">
        <MessageBox messageBoxVisible={messageBoxVisible} messageTitle={messageTitle} messageBody={messageBody} />
      </div>
      <div className="event-form-filters-wrapper">
        <div className="available-presentations-title">
          <span>Choose Events</span>
        </div>
        <form className="filter-form">
          <div className="form-item-wrapper input category">
            <FormControl className="filter-select" size="small">
              <InputLabel sx={{ fontSize: "14px" }} size="small" id="library-select-label">
                Category
              </InputLabel>
              <Select
                labelId="category-select-label"
                id="category-select"
                value={eventCategory}
                label="Category"
                sx={{
                  fontSize: "14px",
                }}
                onChange={(e) => {
                  setEventCategory(e.target.value);
                }}
              >
                {renderCategoryList}
              </Select>
            </FormControl>
          </div>
          <div className="form-item-wrapper">
            <div className="form-submit-wrapper form-button-wrapper form-control-wrapper">
              <Button
                type="submit"
                onClick={updateEventItemView}
                variant="contained"
                sx={{
                  backgroundColor: "#1d9bf1",
                  width: "100%",
                  lineHeight: "14px",
                  height: "35px",
                  fontWeight: "500",
                  fontSize: "14px",
                  textTransform: "none",
                }}
              >
                Apply
              </Button>
            </div>
          </div>
        </form>
      </div>
      <div id="event-view-content" className="event-view-content">
        <div className="event-items-wrapper">
          <div id="event-items">{renderEventItems}</div>
          <div className="">{renderPagination()}</div>
        </div>
        <div className="selected-event-header-wrapper">
          <span>Selected Events</span>
          {selectedEventItems.length > 0 ? (
            <Button
              variant="outlined"
              size="small"
              sx={{
                textTransform: "none",
                fontSize: "14px",
              }}
              className="selected-event-clear-all-button"
              onClick={() => removeAllSelectedEventItems()}
            >
              Clear all
            </Button>
          ) : null}
        </div>
        <div id="event-items-selected-by-user-wrapper" className="event-items-selected-by-user-wrapper">
          <div id="event-items-selected-by-user" className="event-items-selected-by-user">
            {selectedEventItems.length > 0 ? (
              ""
            ) : (
              <>
                <h5 className="instruction title">INSTRUCTIONS</h5>
              </>
            )}
            {selectedEventItems.length > 0 ? (
              <>
                {selectedEventItems.map((event, index) => (
                  <SelectedEventItem
                    key={`selected-event-item-${index}`}
                    removeSelectedEventItem={(event) => removeSelectedEventItem(event)}
                    event={event}
                  />
                ))}
              </>
            ) : (
              <>{contentTypeData.help && <>{contentTypeData.help}</>}</>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
